import React from "react";

/**
 * Inline SVG icon registry for checkout components.
 *
 * We don't pull in lucide-react or react-icons — the registry is a small
 * finite set, inlining avoids a dep and lets us tune stroke weight and
 * sizing via per-store CSS vars (`currentColor`, 1.5 stroke) for a
 * coherent whitelabel look.
 *
 * Every icon follows the same drawing rules so they visually match:
 *   - 24×24 viewBox
 *   - fill="none", stroke="currentColor", strokeWidth={1.5}
 *   - strokeLinecap + strokeLinejoin = "round"
 *   - No fill on any path (purely linework, mirrors the carolinesvedbom.com
 *     checkout aesthetic)
 *
 * Stores opt into an icon by name via `branding.checkout.stepIcons`:
 *   "information" | "shipping" | "payment" | "review" | "truck" |
 *   "shield-check" | "check-circle" | "alert-circle" | "package" |
 *   "credit-card" | "receipt" | "user"
 *
 * Unknown names fall back to a neutral `fallback` icon so a missing
 * config never renders a blank circle.
 */

export type IconName =
  | "information"
  | "shipping"
  | "package"
  | "truck"
  | "payment"
  | "credit-card"
  | "review"
  | "shield-check"
  | "check-circle"
  | "alert-circle"
  | "info-circle"
  | "shopping-bag"
  | "receipt"
  | "user"
  | "fallback";

type IconProps = {
  name?: string | null;
  size?: number;
  className?: string;
  ariaHidden?: boolean;
  title?: string;
};

const COMMON_PATH_PROPS = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

function User() {
  return (
    <>
      <path d="M16 14a4 4 0 1 0-8 0" {...COMMON_PATH_PROPS} />
      <circle cx="12" cy="8" r="4" {...COMMON_PATH_PROPS} />
      <path d="M4 21a8 8 0 0 1 16 0" {...COMMON_PATH_PROPS} />
    </>
  );
}

function Package() {
  return (
    <>
      <path
        d="M12 3 3 7.5v9L12 21l9-4.5v-9L12 3Z"
        {...COMMON_PATH_PROPS}
      />
      <path d="M3 7.5 12 12l9-4.5" {...COMMON_PATH_PROPS} />
      <path d="M12 12v9" {...COMMON_PATH_PROPS} />
      <path d="M7.5 5.25 16.5 9.75" {...COMMON_PATH_PROPS} />
    </>
  );
}

function Truck() {
  return (
    <>
      <path d="M3 7h11v10H3z" {...COMMON_PATH_PROPS} />
      <path d="M14 10h4l3 3v4h-7" {...COMMON_PATH_PROPS} />
      <circle cx="7" cy="18" r="2" {...COMMON_PATH_PROPS} />
      <circle cx="17" cy="18" r="2" {...COMMON_PATH_PROPS} />
    </>
  );
}

function CreditCard() {
  return (
    <>
      <rect x="2.5" y="5.5" width="19" height="13" rx="2" {...COMMON_PATH_PROPS} />
      <path d="M2.5 10h19" {...COMMON_PATH_PROPS} />
      <path d="M6 15h4" {...COMMON_PATH_PROPS} />
    </>
  );
}

function ShieldCheck() {
  return (
    <>
      <path
        d="M12 3 4 6v6c0 4.5 3.5 8 8 9 4.5-1 8-4.5 8-9V6l-8-3Z"
        {...COMMON_PATH_PROPS}
      />
      <path d="m9 12 2 2 4-4" {...COMMON_PATH_PROPS} />
    </>
  );
}

function CheckCircle() {
  return (
    <>
      <circle cx="12" cy="12" r="9" {...COMMON_PATH_PROPS} />
      <path d="m8.5 12 2.5 2.5 4.5-5" {...COMMON_PATH_PROPS} />
    </>
  );
}

function AlertCircle() {
  return (
    <>
      <circle cx="12" cy="12" r="9" {...COMMON_PATH_PROPS} />
      <path d="M12 8v5" {...COMMON_PATH_PROPS} />
      <circle cx="12" cy="16.5" r="0.6" fill="currentColor" />
    </>
  );
}

function Receipt() {
  return (
    <>
      <path
        d="M5 3h14v18l-2.5-1.5L14 21l-2.5-1.5L9 21l-2.5-1.5L4 21V3Z"
        {...COMMON_PATH_PROPS}
      />
      <path d="M8 8h8M8 12h8M8 16h5" {...COMMON_PATH_PROPS} />
    </>
  );
}

function InfoCircle() {
  return (
    <>
      <circle cx="12" cy="12" r="9" {...COMMON_PATH_PROPS} />
      <path d="M12 16v-5" {...COMMON_PATH_PROPS} />
      <circle cx="12" cy="8" r="0.6" fill="currentColor" />
    </>
  );
}

function ShoppingBag() {
  return (
    <>
      <path
        d="M5 8h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 8Z"
        {...COMMON_PATH_PROPS}
      />
      <path d="M9 8V6a3 3 0 0 1 6 0v2" {...COMMON_PATH_PROPS} />
    </>
  );
}

function Check() {
  return <path d="m5 12 5 5 9-11" {...COMMON_PATH_PROPS} />;
}

function Fallback() {
  return <circle cx="12" cy="12" r="4" {...COMMON_PATH_PROPS} />;
}

const REGISTRY: Record<IconName, () => JSX.Element> = {
  information: User,
  user: User,
  shipping: Package,
  package: Package,
  truck: Truck,
  payment: CreditCard,
  "credit-card": CreditCard,
  review: ShieldCheck,
  "shield-check": ShieldCheck,
  "check-circle": CheckCircle,
  "alert-circle": AlertCircle,
  "info-circle": InfoCircle,
  "shopping-bag": ShoppingBag,
  receipt: Receipt,
  fallback: Fallback,
};

/**
 * Small inline check for the "step is completed" badge (smaller stroke).
 */
export function CheckMark({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
    >
      <Check />
    </svg>
  );
}

export function Icon({ name, size = 20, className, ariaHidden = true, title }: IconProps) {
  const key = (name as IconName) in REGISTRY ? (name as IconName) : "fallback";
  const Body = REGISTRY[key];
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden={ariaHidden}
      role={title ? "img" : undefined}
      className={className}
    >
      {title ? <title>{title}</title> : null}
      <Body />
    </svg>
  );
}

export default Icon;
